import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Users, Award, Target, Heart, ArrowRight, Sparkles, Rocket } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const About = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeTeamMember, setActiveTeamMember] = useState(0);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTeamMember((prev) => (prev + 1) % team.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const team = [
    {
      name: "Ferdinand Straka",
      role: "CEO & AI Specialist",
      bio: "Visionary leader with deep expertise in artificial intelligence and business strategy. Ferdinand drives our mission to make AI accessible to businesses of all sizes while maintaining the highest standards of innovation and customer satisfaction.",
      image: "/ferdinandstraka.jpeg",
      achievements: ["AI Strategy Expert", "Business Leadership", "Innovation Pioneer"]
    },
    {
      name: "Max Kuzica",
      role: "Software Engineer",
      bio: "Full-stack developer with a passion for creating scalable and user-friendly applications. Max brings technical excellence to our platform, ensuring robust performance and seamless user experiences across all our solutions.",
      image: "/maxkuzica.jpeg",
      achievements: ["Full-Stack Development", "System Architecture", "Performance Optimization"]
    },
    {
      name: "Radovan Čač",
      role: "AI Specialist",
      bio: "Machine learning expert specializing in natural language processing and conversational AI. Radovan develops and fine-tunes our AI models to deliver intelligent, context-aware responses that truly understand customer needs.",
      image: "/radovancac.jpeg",
      achievements: ["Machine Learning", "NLP Expert", "AI Model Development"]
    }
  ];

  const values = [
    {
      icon: Target,
      title: "Innovation First",
      description: "We're constantly pushing the boundaries of what's possible with AI technology to deliver cutting-edge solutions.",
      color: "from-blue-400 to-cyan-500"
    },
    {
      icon: Heart,
      title: "Customer-Centric",
      description: "Every feature we build is designed with your business success and customer satisfaction in mind.",
      color: "from-violet-400 to-purple-500"
    },
    {
      icon: Users,
      title: "Collaborative Growth",
      description: "We believe in growing together with our clients, providing ongoing support and continuous improvement.",
      color: "from-green-400 to-emerald-500"
    },
    {
      icon: Award,
      title: "Excellence Standard",
      description: "We maintain the highest standards in AI development, security, and customer service delivery.",
      color: "from-amber-400 to-orange-500"
    }
  ];

  return (
    <div className="pt-16 bg-gray-950 overflow-hidden">
      <SEOHead 
        title="About Us - BrixBot AI Customer Service Team" 
        description="Meet the BrixBot team building the future of AI customer service. Learn about our mission, values, and commitment to helping businesses succeed with intelligent chatbots."
        isIndexable={true}
      />
      {/* Hero Section with Parallax */}
      <section className="relative py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-violet-950/30"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        ></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-violet-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 hover:bg-violet-600/30 mb-8 text-lg py-2 px-6">
            <Sparkles className="w-5 h-5 mr-2" />
            Our Story
          </Badge>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
            Building the Future of
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400 animate-pulse">
              Customer Service
            </span>
          </h1>
          
          <p className="text-2xl text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">
            We're on a mission to help every business provide exceptional customer service through 
            intelligent AI chatbots trained on their unique data and expertise.
          </p>
          
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-10 py-6 text-xl rounded-3xl shadow-2xl shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 transform hover:scale-105"
            >
              <Users className="w-6 h-6 mr-3" />
              Get in Touch
              <ArrowRight className="ml-3 w-6 h-6" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-32 bg-gray-900/50 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-500/5 to-purple-500/5"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <Card className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <CardHeader className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Target className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-3xl text-white">Our Mission</CardTitle>
              </CardHeader>
              <CardContent className="relative z-10">
                <p className="text-gray-300 text-lg leading-relaxed">
                  To democratize intelligent customer service by making advanced AI chatbots 
                  accessible and affordable for small and medium businesses. We believe every 
                  business deserves the power of AI to enhance their customer experience.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <CardHeader className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Rocket className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-3xl text-white">Our Vision</CardTitle>
              </CardHeader>
              <CardContent className="relative z-10">
                <p className="text-gray-300 text-lg leading-relaxed">
                  A world where every customer interaction is instant, accurate, and helpful. 
                  We envision AI chatbots that truly understand businesses and their customers, 
                  creating meaningful connections that drive growth and satisfaction.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-32 bg-gray-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-6">
              <Heart className="w-4 h-4 mr-2" />
              Core Values
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Our Values
            </h2>
            <p className="text-xl text-gray-300">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="bg-gray-900/50 border-violet-500/20 text-center backdrop-blur-sm hover:border-violet-400/40 transition-all duration-500 group">
                <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <CardHeader className="relative z-10">
                  <div className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-white text-xl">{value.title}</CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <p className="text-gray-300 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-32 bg-gray-900/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-6">
              <Users className="w-4 h-4 mr-2" />
              Our Team
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-300">
              The experts behind your BrixBot success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {team.map((member, index) => (
              <Card 
                key={index} 
                className={`bg-gray-900/50 border-violet-500/20 backdrop-blur-sm hover:border-violet-400/40 transition-all duration-500 group ${
                  activeTeamMember === index ? 'ring-2 ring-violet-400/50 shadow-2xl shadow-violet-500/20' : ''
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <CardHeader className="text-center relative z-10">
                  <div className="relative mb-6">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-24 h-24 rounded-full mx-auto object-cover ring-2 ring-violet-500/30 group-hover:ring-violet-400/50 transition-all duration-300"
                    />
                    <div className="absolute -inset-1 bg-gradient-to-r from-violet-500 to-purple-600 rounded-full blur opacity-20 group-hover:opacity-40 transition-opacity duration-300"></div>
                  </div>
                  <CardTitle className="text-white text-xl">{member.name}</CardTitle>
                  <CardDescription className="text-violet-400 font-medium text-lg">
                    {member.role}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="text-center relative z-10">
                  <p className="text-gray-300 text-sm mb-4 leading-relaxed">{member.bio}</p>
                  
                  <div className="space-y-2 mb-6">
                    {member.achievements.map((achievement, achievementIndex) => (
                      <Badge key={achievementIndex} className="bg-violet-600/20 text-violet-300 border-violet-500/30 text-xs">
                        {achievement}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-r from-violet-900/30 to-purple-900/30 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-8 text-lg py-2 px-6">
            <Sparkles className="w-5 h-5 mr-2" />
            Join Our Mission
          </Badge>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Join Our
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400">
              Success Story?
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            Let's work together to transform your customer service with intelligent AI chatbots.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-12 py-8 text-2xl rounded-3xl shadow-2xl shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 transform hover:scale-105"
              >
                <Sparkles className="w-6 h-6 mr-3" />
                Get Started Today
              </Button>
            </Link>
            
            <Link to="/contact">
              <Button 
                variant="outline" 
                size="lg" 
                className="border-violet-500/50 text-violet-300 hover:bg-violet-600/10 hover:border-violet-400 px-12 py-8 text-2xl rounded-3xl backdrop-blur-sm transition-all duration-300"
              >
                <Users className="w-6 h-6 mr-3" />
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;